// Compact JWS (RFC 7515) over the two curves a DID document may list, matching
// did_toolkit.jws: ES256 (raw r||s) and EdDSA. The algorithm must match the
// key; "none", HMAC and every other alg are refused. The protected header is
// serialised with sorted keys and no whitespace.
import { ed25519 } from "@noble/curves/ed25519";
import { p256 } from "@noble/curves/p256";
import { sha256 } from "@noble/hashes/sha256";
import { b64u, b64uDecode, fromUtf8, utf8 } from "./encoding";
import { canonicalString } from "./jcs";
import type { KeyKind, PublicKey } from "./keys";

export type Alg = "ES256" | "EdDSA";
export const ALG_FOR_KIND: Record<KeyKind, Alg> = { "P-256": "ES256", "Ed25519": "EdDSA" };

/** Anything that can sign: a WebCrypto key, a phrase-derived key, a test key. */
export interface Signer {
  alg: Alg;
  kid: string;              // absolute DID URL
  publicKey: PublicKey;
  sign(message: Uint8Array): Promise<Uint8Array>;   // raw 64-byte signature
}

export const protectedHeader = (header: Record<string, unknown>): string =>
  b64u(utf8(canonicalString(header)));

export function signingInput(header: Record<string, unknown>, payload: Uint8Array): Uint8Array {
  return utf8(`${protectedHeader(header)}.${b64u(payload)}`);
}

export async function signCompact(signer: Signer, payload: Uint8Array): Promise<string> {
  const header = { alg: signer.alg, kid: signer.kid };
  const input = signingInput(header, payload);
  const sig = await signer.sign(input);
  if (sig.length !== 64) throw new Error(`${signer.alg} signatures are 64 raw bytes`);
  return `${fromUtf8(input)}.${b64u(sig)}`;
}

export interface ParsedJws {
  header: { alg: string; kid?: string; [k: string]: unknown };
  payload: Uint8Array;
  signature: Uint8Array;
  signingInput: Uint8Array;
}

export function parseCompact(jws: string): ParsedJws {
  if (typeof jws !== "string") throw new Error("a JWS is a string");
  const parts = jws.split(".");
  if (parts.length !== 3) throw new Error("a compact JWS has three segments");
  const header = JSON.parse(fromUtf8(b64uDecode(parts[0])));
  if (typeof header !== "object" || header === null) throw new Error("bad JWS header");
  return { header, payload: b64uDecode(parts[1]), signature: b64uDecode(parts[2]),
           signingInput: utf8(`${parts[0]}.${parts[1]}`) };
}

/** Verify raw signature bytes under a key; the algorithm must follow the key. */
export function verifyRaw(alg: string, pk: PublicKey, message: Uint8Array, signature: Uint8Array): boolean {
  if (alg !== ALG_FOR_KIND[pk.kind]) return false;
  try {
    if (alg === "EdDSA") return ed25519.verify(signature, message, pk.raw);
    if (alg === "ES256") return p256.verify(signature, sha256(message), pk.raw, { lowS: false, prehash: false });
  } catch { return false; }
  return false;
}

export function verifyCompact(jws: string, pk: PublicKey): ParsedJws | null {
  const p = parseCompact(jws);
  return verifyRaw(String(p.header.alg), pk, p.signingInput, p.signature) ? p : null;
}

export const payloadJson = (p: ParsedJws): any => JSON.parse(fromUtf8(p.payload));
